import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { NimmstaCommonsModuleConfiguration } from '../nimmsta-commons-module.config';
import { AuthService } from '../services/auth.service';
import { KeycloakSessionService } from '../services/keycloak-session.service';
import { KeycloakDevice, KeycloakSession } from '../models/keycloak-session';
import { IMPERSONATION_SESSION_STORAGE_KEY } from '../utils/constants.impersonation';
import { HeaderBarComponent } from '../components/header-bar/header-bar.component';
import { LoadingIndicatorComponent } from '../components/loading-indicator/loading-indicator.component';
import { ErrorComponent } from '../components/error/error.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DateTimePipe } from '../pipes/date-time.pipe';
import { NimmstaTimeAgoPipe } from '../pipes/nimmsta-timeago.pipe';

marker('profile-session.title');

@Component({
    selector: 'app-profile-session',
    templateUrl: './profile-session.component.html',
    imports: [HeaderBarComponent, LoadingIndicatorComponent, ErrorComponent, TranslatePipe, DateTimePipe, NimmstaTimeAgoPipe],
})
export class ProfileSessionComponent implements OnInit {
  error?: HttpErrorResponse;

  devices?: KeycloakDevice[];

  isImpersonating: boolean;

  isLoading = true;

  private sessionService = inject(KeycloakSessionService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private configuration = inject(NimmstaCommonsModuleConfiguration);

  public constructor() {
    this.isImpersonating = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY) !== null;
  }

  ngOnInit(): void {
    this.loadDevices();
  }

  private loadDevices() {
    this.isLoading = true;
    this.sessionService.getActiveDevices().subscribe({
      next: (devices) => {
        this.isLoading = false;
        this.devices = devices;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.error = error;
      },
    });
  }

  protected deviceSessions(device: KeycloakDevice): KeycloakSession[] {
    // Show only sessions for the platform client
    const sessions = device.sessions.filter(
      (session) => session.clients.some((client) => client.clientId === this.configuration.keycloakClientId),
    );

    // Move the current session to the front of the list
    const currentSession = sessions.find((session) => session.current);
    if (currentSession) {
      sessions.splice(sessions.indexOf(currentSession), 1);
      sessions.unshift(currentSession);
    }

    return sessions;
  }

  protected terminateSession(session: KeycloakSession) {
    if (this.isImpersonating) {
      return;
    }

    this.sessionService.terminate(session.id).subscribe({
      next: () => {
        if (session.current) {
          this.devices = undefined;
          return this.authService.logout(this.router.url.split('?')[0]);
        }
        this.loadDevices();
        return Promise.resolve();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  protected terminateAllSessions() {
    if (this.isImpersonating) {
      return;
    }

    this.sessionService.terminateAll().subscribe({
      next: () => {
        this.authService.logout(window.location.origin);
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  protected clientNames(session: KeycloakSession): string {
    return session.clients.map((client) => client.clientId).join(', ');
  }
}
